import { useState } from "react";
import { API } from "../config";

export default function Admin() {
  const [user, setUser] = useState("admin");
  const [pass, setPass] = useState("");
  const [items, setItems] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [startAt, setStartAt] = useState("");
  const [notice, setNotice] = useState("");
  const [auth, setAuth] = useState("");
  const [gallery, setGallery] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);

  async function loadGallery(token) {
    const r = await fetch(`${API}/gallery`);
    const d = await r.json();
    setGallery(d.items || []);
  }

  async function uploadPhoto(e) {
    e.preventDefault();
    const fileInput = document.getElementById("gallery-file");
    if (!fileInput.files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileInput.files[0]);
      fd.append("caption", caption);
      const r = await fetch(`${API}/admin/gallery`, { method: "POST", headers: { Authorization: "Basic " + auth }, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      setNotice("Photo added to gallery.");
      setCaption("");
      fileInput.value = "";
      await loadGallery(auth);
    } catch (err) {
      setNotice(String(err.message || err));
    } finally {
      setUploading(false);
    }
  }

  async function deletePhoto(id) {
    await fetch(`${API}/admin/gallery/${id}`, { method: "DELETE", headers: { Authorization: "Basic " + auth } });
    await loadGallery(auth);
  }

  async function load(token) {
    const r = await fetch(`${API}/admin/schedule`, { headers: { Authorization: "Basic " + token } });
    if (!r.ok) throw new Error("Unable to authenticate or load appointments");
    const d = await r.json();
    setItems(d.appointments || []);
    setBlocks(d.blocks || []);
  }

  async function login(e) {
    e.preventDefault();
    try {
      const token = btoa(user + ":" + pass);
      await load(token);
      await loadGallery(token);
      setAuth(token);
      setNotice("Schedule loaded.");
    } catch (err) {
      setNotice(String(err.message || err));
    }
  }

  async function action(a, id) {
    try {
      const r = await fetch(`${API}/admin/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Basic " + auth },
        body: JSON.stringify({ action: a, id, startAt }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || "Action failed");
      setNotice(d.checkoutUrl
        ? `Approved. Payment link: ${d.checkoutUrl} ${d.emailSent ? "(email sent)" : "(email not configured — copy link to customer)"}`
        : "Updated.");
      await load(auth);
    } catch (err) {
      setNotice(String(err.message || err));
    }
  }

  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" });

  return (
    <main className="shell inner">
      <h1 className="page-title">Scheduling Administration</h1>
      {!auth ? (
        <form onSubmit={login} className="admin-login" data-testid="admin-login-form">
          <label>Admin username
            <input value={user} onChange={(e) => setUser(e.target.value)} data-testid="admin-user" />
          </label>
          <label>Admin password
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} data-testid="admin-pass" />
          </label>
          <button className="button dark" data-testid="admin-login-btn">Sign in</button>
        </form>
      ) : (
        <>
          <h2>Appointments & requests</h2>
          <div className="admin-list" data-testid="admin-appointments">
            {items.map((a) => (
              <article key={a.id} className="admin-item" data-testid={`admin-item-${a.id}`}>
                <strong>{fmt(a.start_at)} · {a.customer_name}</strong>
                <p>{a.kind} · {a.status} · {a.customer_email} · {a.customer_phone}</p>
                <p>{a.location} · {a.garments}</p>
                {a.status === "pending_approval" && (
                  <button className="button gold" onClick={() => action("approve", a.id)} data-testid={`approve-${a.id}`}>
                    Approve & Send $150 Checkout
                  </button>
                )}
                {a.status !== "canceled" && (
                  <button className="button subtle" onClick={() => action("cancel", a.id)} data-testid={`cancel-${a.id}`}>
                    Cancel / release slot
                  </button>
                )}
              </article>
            ))}
          </div>
          <h2>Block a time</h2>
          <p>Enter a UTC timestamp from an appointment slot (ISO 8601), e.g. 2026-11-09T16:00:00.000Z.</p>
          <input value={startAt} onChange={(e) => setStartAt(e.target.value)} placeholder="YYYY-MM-DDTHH:mm:ss.000Z" data-testid="block-input" />
          <button className="button dark" onClick={() => action("block")} data-testid="block-btn">Block slot</button>
          <h3>Blocked slots</h3>
          {blocks.map((b) => (
            <p key={b.start_at}>
              {b.start_at}{" "}
              <button onClick={() => setStartAt(b.start_at)}>Select</button>{" "}
              <button onClick={async () => {
                await fetch(`${API}/admin/action`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: "Basic " + auth },
                  body: JSON.stringify({ action: "unblock", startAt: b.start_at }),
                });
                await load(auth);
              }}>Unblock</button>
            </p>
          ))}
          <h2>Gallery photos</h2>
          <p>Add photos of your work — they appear on the public Gallery page instantly.</p>
          <form onSubmit={uploadPhoto} className="admin-login" data-testid="gallery-upload-form">
            <label>Photo (jpg, png or webp, max 15 MB)
              <input id="gallery-file" type="file" accept=".jpg,.jpeg,.png,.webp" data-testid="gallery-file" />
            </label>
            <label>Caption (optional)
              <input value={caption} maxLength={200} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Wedding suit alteration, 2024" data-testid="gallery-caption" />
            </label>
            <button className="button dark" disabled={uploading} data-testid="gallery-upload-btn">{uploading ? "Uploading…" : "Add to gallery"}</button>
          </form>
          <div className="gallery-grid" data-testid="admin-gallery-grid">
            {gallery.map((g) => (
              <figure key={g.id} className="gallery-item">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${g.url}`} alt={g.caption || "Gallery"} loading="lazy" />
                <figcaption>
                  {g.caption}{" "}
                  <button onClick={() => deletePhoto(g.id)} data-testid={`gallery-delete-${g.id}`}>Delete</button>
                </figcaption>
              </figure>
            ))}
          </div>
        </>
      )}
      {notice && <p role="status" className={"form-message" + (notice.toLowerCase().includes("unable") || notice.toLowerCase().includes("failed") ? " error" : "")} data-testid="admin-notice">{notice}</p>}
    </main>
  );
}
